import { useState } from "react";
import { startOfWeek, format } from "date-fns";
import {
  ONE_WEEK_OF_MS,
  FORMAT_DAY_TIME,
  FORMAT_READABLE_DATE,
} from "reports/utils/timeUtils";
import { DataDumpType, ReportParams } from "../../../types";
import DataDumpGetter from "../../../getters/DataDumpGetter";
import { useGetIsOperatingSystemWindows } from "utils/customHooks/useGetIsOperatingSystemWindows";
import {
  getNumberAndDurationOfSegmentsByCategory,
  getSegmentsForCategoryInOrder,
  getNumberAndDurationOfSegmentsByMachine,
} from "../../../components/Reports/reportUtils";
import SterisSummaryTable from "./SterisSummaryTable";
import { startCase } from "lodash";
import LoadingCircle from "reports/components/simple/LoadingCircle";
import ReportHeader from "../../../components/Reports/ReportHeader";
import PrintButton from "../../../components/Reports/Buttons/PrintButton";
import { midgardClients } from "providers/MidgardProvider";
import { useQuery } from "@apollo/client";
import SterisDowntimeEventChart from "./SterisDowntimeEventChart";
import styled from "styled-components/macro";
import { PrintableReportStyles } from "../../../components/Reports/printableReportContentStyles";
import PrintableRechart from "../../../components/Reports/PrintableRechart";
import { PNL_CATEGORIES_TO_COLORS_MAP } from "../../../constants/constants";
import { useParams } from "react-router";
import { PROCESS_GROUPS_QUERY } from "components/DataGetter/ProcessGroupsGetter";

const SterisWeeklyStyles = styled.div`
  display: flex;
  flex-direction: column;
  width: 90vw;
  margin: 0 auto;

  .summaryTables {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-column-gap: 2em;
  }

  @media print {
    width: 100vw;

    .summaryTables {
      padding: 1em 2em;
    }
  }
`;

export default function SterisWeekly() {
  const { ckey: tenant } = useParams() as ReportParams;
  const isWindows = useGetIsOperatingSystemWindows();

  const {
    loading,
    error,
    data: processGroupsData,
  } = useQuery(PROCESS_GROUPS_QUERY, {
    client: midgardClients.get(tenant),
  });

  const currentTime = Date.now();

  // Report runs weekly from Sunday - Sunday, 6AM Eastern
  const lt = startOfWeek(currentTime).setHours(6, 0, 0, 0);
  const ge = lt - ONE_WEEK_OF_MS;

  const reportDataFormatted = `${format(ge, FORMAT_DAY_TIME)} - ${format(
    lt,
    FORMAT_DAY_TIME
  )}`;

  const [dataDumps, setDataDumps] = useState([] as DataDumpType[]);

  if (loading) {
    return <LoadingCircle />;
  }
  if (error) {
    return <div>`Error! ${error.message}`</div>;
  }

  // This report is for the Molding Group for Steris
  const moldingProcessGroup = processGroupsData.processGroups.find(
    ({ name }) => name === "Molding"
  );

  // Get machemetry ids of this process group for query
  const machemetryIds = moldingProcessGroup?.processes?.map(
    ({ taps }) => taps[0].machemetryId
  );

  // Categories we want to show charts for
  const downtimeCatgories = ["planned", "waiting"];

  document.title =
    "RAVEN // Steris - Weekly Report - " + format(ge, FORMAT_READABLE_DATE);

  const isLandscape = true;

  return (
    <>
      <DataDumpGetter
        range={{ ge: String(ge), lt: String(lt) }}
        machemetryIds={machemetryIds}
        setData={setDataDumps}
      />
      {dataDumps?.length <= 0 ? (
        <LoadingCircle />
      ) : (
        downtimeCatgories.map(category => {
          const SterisChart = (width: number) => (
            <SterisDowntimeEventChart
              title={`${startCase(category)} Downtimes`}
              data={getSegmentsForCategoryInOrder({
                dataDumps,
                category,
              })}
              width={width}
              color={PNL_CATEGORIES_TO_COLORS_MAP[category]}
            />
          );

          return (
            <div key={category}>
              <ReportHeader
                title={`${startCase(category)} Downtime Efficiency Analysis`}
                subheader={`${tenant.toUpperCase()} - Molding Room`}
                dateString={reportDataFormatted}
                rightSideComponent={<PrintButton />}
              />
              <PrintableReportStyles
                $isWindows={isWindows}
                $isLandscape={isLandscape}
              >
                <SterisWeeklyStyles>
                  <div className="summaryTables">
                    <SterisSummaryTable
                      title={`${startCase(category)} Downtimes By Tag`}
                      data={
                        getNumberAndDurationOfSegmentsByCategory(dataDumps)[
                          category
                        ]
                      }
                    />
                    <SterisSummaryTable
                      title={`${startCase(category)} Downtimes By Machine`}
                      data={getNumberAndDurationOfSegmentsByMachine({
                        dataDumps,
                        category,
                      })}
                      skipTotal={true}
                    />
                  </div>
                  <PrintableRechart
                    chart={SterisChart}
                    isLandscape={isLandscape}
                  />
                </SterisWeeklyStyles>
              </PrintableReportStyles>
            </div>
          );
        })
      )}
    </>
  );
}
